import { useState } from "react";
import { Play } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";

const TOURS = [
  {
    id: "getting-started",
    title: "Getting started with Fieldlink",
    sub: "Setting up clients and jobs, timesheets in the field, payroll and connecting Xero.",
    len: "2:30",
    mp4: "/tour/fieldlink-tour.mp4",
    webm: "/tour/fieldlink-tour.webm",
  },
  {
    id: "going-further",
    title: "Going further — costing, QR sign-ins & fleet",
    sub: "Live back-costing and margins, QR site sign-in posters, fleet WoF and servicing, truck dispatch, safety hub and invoicing.",
    len: "2:09",
    mp4: "/tour/fieldlink-tour-2.mp4",
    webm: "/tour/fieldlink-tour-2.webm",
  },
  {
    id: "crew-and-pm",
    title: "Crew dispatch, chat & the PM toolkit",
    sub: "Dispatching the crew and how they see it on their phone, in-house chat, project management, the site diary and SSSPs.",
    len: "1:39",
    mp4: "/tour/fieldlink-tour-3.mp4",
    webm: "/tour/fieldlink-tour-3.webm",
  },
];

function TourDialog({ tour, onClose }) {
  return (
    <Dialog open={!!tour} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl p-0 overflow-hidden bg-black border-white/10" data-testid="tour-video-dialog">
        <DialogTitle className="sr-only">{tour?.title || "Fieldlink tour"}</DialogTitle>
        {tour && (
          <video controls autoPlay playsInline className="w-full aspect-video bg-black" data-testid="tour-video">
            <source src={tour.mp4} type="video/mp4" />
            {tour.webm && <source src={tour.webm} type="video/webm" />}
          </video>
        )}
      </DialogContent>
    </Dialog>
  );
}

// "Watch the tour" — chip button used in the dashboard hero (plays part 1).
export function TourVideoButton() {
  const [tour, setTour] = useState(null);
  return (
    <>
      <button
        type="button"
        onClick={() => setTour(TOURS[0])}
        className="inline-flex items-center gap-2 rounded-lg bg-black/45 border border-white/20 text-white backdrop-blur px-3 py-1.5 text-[12px] font-semibold hover:bg-[#FF5F15] hover:border-[#FF5F15] transition-colors"
        data-testid="watch-tour-btn"
      >
        <Play className="w-3.5 h-3.5 text-[#FF7A18]" fill="currentColor" />
        Watch the tour
      </button>
      <TourDialog tour={tour} onClose={() => setTour(null)} />
    </>
  );
}

// Banner cards for help/support pages — anyone can play the walkthroughs.
export function TourVideoCard() {
  const [tour, setTour] = useState(null);
  return (
    <>
      <div className="grid gap-3 mb-6">
        {TOURS.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTour(t)}
            className="group w-full text-left rounded-[14px] border border-[#FF5F15]/30 bg-gradient-to-r from-[#FF5F15]/10 to-transparent dark:from-[#FF5F15]/15 p-4 sm:p-5 flex items-center gap-4 hover:border-[#FF5F15]/60 transition-colors"
            data-testid={`tour-video-card-${t.id}`}
          >
            <span className="w-12 h-12 shrink-0 rounded-full bg-[#FF5F15] flex items-center justify-center shadow-lg shadow-[#FF5F15]/30 group-hover:scale-105 transition-transform">
              <Play className="w-5 h-5 text-white ml-0.5" fill="currentColor" />
            </span>
            <span className="min-w-0">
              <span className="block font-bold text-[15px] text-zinc-900 dark:text-white">{t.title}</span>
              <span className="block text-[12.5px] text-zinc-500 dark:text-zinc-400 mt-0.5">{t.sub}</span>
            </span>
            <span className="ml-auto hidden sm:inline text-[11px] font-bold uppercase tracking-wider text-[#FF5F15] whitespace-nowrap">{t.len} →</span>
          </button>
        ))}
      </div>
      <TourDialog tour={tour} onClose={() => setTour(null)} />
    </>
  );
}
